import React, { useState, useEffect } from 'react';
import { inverterPowerService, HeatmapGridData, HeatmapBand } from '../../services/inverterPowerService';
import LegendBar from './LegendBar';
import BlockInverterDrilldown from './BlockInverterDrilldown';

interface ActivePowerHeatmapProps {
  date?: string;
  plantId?: string;
}

const ActivePowerHeatmap: React.FC<ActivePowerHeatmapProps> = ({ date, plantId }) => {
  const [gridData, setGridData] = useState<HeatmapGridData | null>(null);
  const [bands, setBands] = useState<HeatmapBand[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCell, setSelectedCell] = useState<{
    blockId: string;
    inverterId: string;
    power: number;
  } | null>(null);

  useEffect(() => {
    loadData();
  }, [date, plantId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [powerData, bandsData] = await Promise.all([
        inverterPowerService.getActivePower(date, plantId),
        inverterPowerService.getHeatmapBands()
      ]);
      setGridData(powerData);
      setBands(bandsData.bands);
    } catch (error) {
      console.error('Error loading heatmap data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getColorForPower = (power: number): string => {
    for (const band of bands) {
      if (power >= band.lower_bound && power <= band.upper_bound) {
        return band.color_code;
      }
    }
    return '#F3F4F6'; // Default gray
  };

  const handleCellClick = (blockId: string, inverterId: string, power: number) => {
    setSelectedCell({ blockId, inverterId, power });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse bg-gray-200 dark:bg-gray-700 w-full h-full rounded-lg"></div>
      </div>
    );
  }

  if (!gridData) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        No data available
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Inverters Active Power Heatmap</h3>
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {gridData.summary.total_inverters} inverters across {gridData.summary.total_blocks} blocks
        </div>
      </div>

      {/* Legend */}
      <LegendBar bands={bands} />

      {/* Heatmap Grid */}
      <div className="overflow-auto border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
        <table className="min-w-full">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-700">
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Block / Inverter
              </th>
              {gridData.inverters.map(inverterId => (
                <th key={inverterId} className="px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {inverterId}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {gridData.blocks.map(blockId => (
              <tr key={blockId} className="border-t border-gray-200 dark:border-gray-700">
                <td className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-700">
                  {blockId}
                </td>
                {gridData.inverters.map(inverterId => {
                  const cellData = gridData.grid_data[blockId]?.[inverterId];
                  const power = cellData?.power || 0;
                  const backgroundColor = getColorForPower(power);
                  
                  return (
                    <td
                      key={`${blockId}-${inverterId}`}
                      className="px-2 py-2 text-center text-xs cursor-pointer hover:opacity-80 transition-opacity text-gray-900"
                      style={{ backgroundColor }}
                      onClick={() => handleCellClick(blockId, inverterId, power)}
                      title={`${blockId}-${inverterId}: ${power}kW`}
                    >
                      {power.toFixed(1)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded border border-blue-200 dark:border-blue-700">
          <div className="text-lg font-semibold text-blue-600 dark:text-blue-400">
            {gridData.summary.avg_power.toFixed(1)}kW
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Average Power</div>
        </div>
        <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded border border-green-200 dark:border-green-700">
          <div className="text-lg font-semibold text-green-600 dark:text-green-400">
            {gridData.summary.total_inverters}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Total Inverters</div>
        </div>
        <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded border border-purple-200 dark:border-purple-700">
          <div className="text-lg font-semibold text-purple-600 dark:text-purple-400">
            {gridData.summary.total_blocks}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Total Blocks</div>
        </div>
      </div>

      {/* Drilldown Modal */}
      {selectedCell && (
        <BlockInverterDrilldown
          blockId={selectedCell.blockId}
          inverterId={selectedCell.inverterId}
          power={selectedCell.power}
          onClose={() => setSelectedCell(null)}
        />
      )}
    </div>
  );
};

export default ActivePowerHeatmap;